import React, { useMemo, useState } from 'react';
import { CheckCircle, X, Volume2 } from './Icons';
import { speakTextWithFallback } from '../utils/voiceManager';
import { useSupabaseData } from '../hooks/useSupabaseData';

const StudyMode = ({ 
  word, 
  language = 'traditional', 
  onComplete, 
  onNext 
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [userAnswer, setUserAnswer] = useState('');
  const [isCorrect, setIsCorrect] = useState(null);
  
  // Pronunciation and recording states
  const [isPlaying, setIsPlaying] = useState(false);
  const [playingText, setPlayingText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);

  // Optionally load a vocab word from Supabase if not provided
  const { data: remote } = useSupabaseData({
    table: 'eng_vocabulary',
    mapRow: (row) => ({
      id: row.word_id || row.id,
      english: row.english,
      traditional: row.traditional || row.zh_traditional,
      simplified: row.simplified || row.zh_simplified,
      pronunciation: row.pronunciation || row.phonetic,
      definition: row.definition_en || row.definition,
      example: row.example_en || row.example,
      image: row.image_url || null
    }),
    getFallback: () => null,
    query: { limit: 1 }
  });

  const effectiveWord = useMemo(() => {
    if (word) return word;
    if (Array.isArray(remote) && remote.length > 0) return remote[0];
    return null;
  }, [word, remote]);

  const getDisplayText = () => {
    switch (language) {
      case 'traditional':
        return {
          question: effectiveWord?.english,
          answer: effectiveWord?.traditional,
          hint: effectiveWord?.simplified
        };
      case 'simplified':
        return {
          question: effectiveWord?.english,
          answer: effectiveWord?.simplified,
          hint: effectiveWord?.traditional
        };
      case 'english':
        return {
          question: effectiveWord?.traditional,
          answer: effectiveWord?.english,
          hint: effectiveWord?.simplified
        };
      default:
        return {
          question: effectiveWord?.english,
          answer: effectiveWord?.traditional,
          hint: effectiveWord?.pronunciation
        };
    }
  };

  const displayText = getDisplayText();

  // Enhanced Text-to-Speech function with preferred voice support
  const speakText = (text) => {
    speakTextWithFallback(
      text,
      null, // availableVoices will be loaded by the voice manager
      (playingText) => {
        setIsPlaying(true);
        setPlayingText(playingText);
      },
      () => {
        setIsPlaying(false);
        setPlayingText('');
      },
      (event) => {
        setIsPlaying(false);
        setPlayingText('');
        if (event && event.error === 'not-allowed') {
          alert(language === 'traditional' ? '請允許語音權限' :
                language === 'simplified' ? '请允许语音权限' : 'Please allow speech permission');
        }
      }
    );
  };

  // Enhanced recording functions with better audio quality and format
  const startRecording = async () => {
    try {
      // Request high-quality audio
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100,
          channelCount: 1
        } 
      });
      
      // Use better audio format for compatibility
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'audio/mp4';
      
      const recorder = new MediaRecorder(stream, { mimeType });
      const chunks = [];
      
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };
      
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: mimeType });
        const url = URL.createObjectURL(blob);
        setRecordingUrl(url);
        setIsRecording(false);
        
        // Clean up the stream
        stream.getTracks().forEach(track => track.stop());
      };
      
      recorder.onerror = (event) => {
        console.error('Recording error:', event);
        setIsRecording(false);
        alert(language === 'traditional' ? '錄音失敗' :
              language === 'simplified' ? '录音失败' : 'Recording failed');
      };
      
      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      if (error.name === 'NotAllowedError') {
        alert(language === 'traditional' ? '請允許麥克風權限' :
              language === 'simplified' ? '请允许麦克风权限' : 'Please allow microphone permission');
      } else if (error.name === 'NotFoundError') {
        alert(language === 'traditional' ? '找不到麥克風設備' :
              language === 'simplified' ? '找不到麦克风设备' : 'No microphone found');
      } else {
        alert(language === 'traditional' ? '錄音啟動失敗' :
              language === 'simplified' ? '录音启动失败' : 'Failed to start recording');
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      try {
        mediaRecorder.stop();
      } catch (error) {
        console.error('Error stopping recording:', error);
        setIsRecording(false);
      }
    }
  };

  const playRecording = () => {
    if (recordingUrl) {
      try {
        const audio = new Audio(recordingUrl);
        
        // Add event listeners for better playback control
        audio.onloadstart = () => {
          console.log('Audio loading started');
        };
        
        audio.oncanplay = () => {
          console.log('Audio can play');
        };
        
        audio.onplay = () => {
          console.log('Audio playback started');
        };
        
        audio.onended = () => {
          console.log('Audio playback ended');
        };
        
        audio.onerror = (event) => {
          console.error('Audio playback error:', event);
          alert(language === 'traditional' ? '音頻播放失敗' :
                language === 'simplified' ? '音频播放失败' : 'Audio playback failed');
        };
        
        // Set audio properties for better playback
        audio.volume = 1.0;
        audio.playbackRate = 1.0;
        
        const playPromise = audio.play();
        
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.error('Audio play failed:', error);
            alert(language === 'traditional' ? '音頻播放失敗' :
                  language === 'simplified' ? '音频播放失败' : 'Audio playback failed');
          });
        }
      } catch (error) {
        console.error('Error playing recording:', error);
        alert(language === 'traditional' ? '音頻播放失敗' :
              language === 'simplified' ? '音频播放失败' : 'Audio playback failed');
      }
    }
  };

  const clearRecording = () => {
    if (recordingUrl) {
      URL.revokeObjectURL(recordingUrl);
      setRecordingUrl(null);
    }
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      try {
        mediaRecorder.stop();
      } catch (error) {
        console.error('Error stopping media recorder:', error);
      }
    }
    setIsRecording(false);
  };

  const studySteps = [
    {
      type: 'introduction',
      title: language === 'traditional' ? '學習新詞彙' :
             language === 'simplified' ? '学习新词汇' :
             'Learn New Word'
    },
    {
      type: 'practice',
      title: language === 'traditional' ? '練習記憶' :
             language === 'simplified' ? '练习记忆' :
             'Practice Memory'
    },
    {
      type: 'review',
      title: language === 'traditional' ? '複習確認' :
             language === 'simplified' ? '复习确认' :
             'Review & Confirm'
    }
  ];

  const handleAnswerSubmit = () => {
    const correct = userAnswer.toLowerCase().trim() === displayText.answer.toLowerCase().trim();
    setIsCorrect(correct);
    setShowAnswer(true);
  };

  const handleNextStep = () => {
    if (currentStep < studySteps.length - 1) {
      setCurrentStep(currentStep + 1);
      setShowAnswer(false);
      setUserAnswer('');
      setIsCorrect(null);
    } else {
      onComplete && onComplete();
    }
  };

  const renderIntroduction = () => (
    <div className="text-center space-y-6">
      {effectiveWord?.image && (
        <div className="w-full h-40 rounded-xl overflow-hidden">
          <img 
            src={effectiveWord.image} 
            alt={effectiveWord.english}
            className="w-full h-full object-cover"
          />
        </div>
      )}
      
      <div className="space-y-4">
        <h2 className="text-3xl font-bold text-white">{displayText.answer}</h2>
        <p className="text-xl text-gray-300">{displayText.question}</p>
        <p className="text-lg text-gray-400">{effectiveWord?.pronunciation}</p>
      </div>

      <div className="bg-black/20 rounded-xl p-4">
        <p className="text-gray-200 text-sm leading-relaxed">{effectiveWord?.definition}</p>
      </div>

      <div className="bg-blue-500/20 rounded-xl p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-blue-200 text-sm italic">"{effectiveWord?.example}"</p>
          <button
            onClick={() => effectiveWord?.example && speakText(effectiveWord.example)}
            className={`p-2 rounded-lg transition-all ${
              isPlaying && playingText === effectiveWord?.example
                ? 'bg-orange-600 text-white' 
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
            title={language === 'traditional' ? '發音' : 
                   language === 'simplified' ? '发音' : 'Pronounce'}
          >
            <span className="text-lg">{isPlaying && playingText === effectiveWord?.example ? '⏸️' : '🔊'}</span>
          </button>
        </div>
        
        {/* Recording Controls for Example Sentence */}
        <div className="flex items-center space-x-2 mt-2">
          {!isRecording && !recordingUrl && (
            <button
              onClick={startRecording}
              className="p-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-all"
              title={language === 'traditional' ? '錄音' : 
                     language === 'simplified' ? '录音' : 'Record'}
            >
              <span className="text-lg">🎤</span>
            </button>
          )}
          
          {isRecording && (
            <button
              onClick={stopRecording}
              className="p-2 rounded-lg bg-red-700 text-white transition-all animate-pulse"
              title={language === 'traditional' ? '停止' : 
                     language === 'simplified' ? '停止' : 'Stop'}
            >
              <span className="text-lg">⏹️</span>
            </button>
          )}
          
          {recordingUrl && (
            <>
              <button
                onClick={playRecording}
                className="p-2 rounded-lg bg-green-600 hover:bg-green-700 text-white transition-all"
                title={language === 'traditional' ? '播放' : 
                       language === 'simplified' ? '播放' : 'Play'}
              >
                <span className="text-lg">▶️</span>
              </button>
              
              <button
                onClick={clearRecording}
                className="p-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white transition-all"
                title={language === 'traditional' ? '清除' : 
                       language === 'simplified' ? '清除' : 'Clear'}
              >
                <span className="text-lg">🗑️</span>
              </button>
            </>
          )}
        </div>
      </div>

      <div className="flex space-x-3">
        <button 
          onClick={() => effectiveWord?.english && speakText(effectiveWord.english)}
          className={`flex-1 flex items-center justify-center space-x-2 py-3 rounded-lg transition-all ${
            isPlaying && playingText === effectiveWord?.english
              ? 'bg-orange-600 text-white' 
              : 'bg-blue-500 hover:bg-blue-600 text-white'
          }`}
        >
          <Volume2 className="w-5 h-5" />
          <span>
            {language === 'traditional' ? '聽發音' :
             language === 'simplified' ? '听发音' :
             'Listen'}
          </span>
        </button>
        <button 
          onClick={handleNextStep}
          className="flex-1 py-3 bg-green-500 rounded-lg"
        >
          {language === 'traditional' ? '開始練習' :
           language === 'simplified' ? '开始练习' :
           'Start Practice'}
        </button>
      </div>
    </div>
  );

  const renderPractice = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-semibold mb-2">
          {language === 'traditional' ? '請輸入對應的詞彙' :
           language === 'simplified' ? '请输入对应的词汇' :
           'Enter the corresponding word'}
        </h3>
        <div className="text-2xl font-bold text-blue-400 mb-4">
          {displayText.question}
        </div>
        <p className="text-gray-400 text-sm">
          {language === 'traditional' ? '提示' :
           language === 'simplified' ? '提示' :
           'Hint'}: {displayText.hint}
        </p>
      </div>

      <div className="space-y-4">
        <input
          type="text"
          value={userAnswer}
          onChange={(e) => setUserAnswer(e.target.value)}
          placeholder={language === 'traditional' ? '輸入答案...' :
                      language === 'simplified' ? '输入答案...' :
                      'Enter answer...'}
          className="w-full p-4 bg-gray-800 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none"
        />

        {showAnswer && (
          <div className={`p-4 rounded-xl ${
            isCorrect ? 'bg-green-500/20 border border-green-500' : 'bg-red-500/20 border border-red-500'
          }`}>
            <div className="flex items-center space-x-2 mb-2">
              {isCorrect ? (
                <CheckCircle className="w-5 h-5 text-green-400" />
              ) : (
                <X className="w-5 h-5 text-red-400" />
              )}
              <span className={`font-semibold ${isCorrect ? 'text-green-400' : 'text-red-400'}`}>
                {isCorrect ? 
                  (language === 'traditional' ? '正確！' :
                   language === 'simplified' ? '正确！' :
                   'Correct!') :
                  (language === 'traditional' ? '不正確' :
                   language === 'simplified' ? '不正确' :
                   'Incorrect')
                }
              </span>
            </div>
            <p className="text-gray-300">
              {language === 'traditional' ? '正確答案：' :
               language === 'simplified' ? '正确答案：' :
               'Correct answer: '}{displayText.answer}
            </p>
          </div>
        )}

        <div className="flex space-x-3">
          {!showAnswer ? (
            <button 
              onClick={handleAnswerSubmit}
              disabled={!userAnswer.trim()}
              className="flex-1 py-3 bg-blue-500 rounded-lg disabled:opacity-50"
            >
              {language === 'traditional' ? '提交答案' :
               language === 'simplified' ? '提交答案' :
               'Submit Answer'}
            </button>
          ) : (
            <button 
              onClick={handleNextStep}
              className="flex-1 py-3 bg-green-500 rounded-lg"
            >
              {language === 'traditional' ? '繼續' :
               language === 'simplified' ? '继续' :
               'Continue'}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const renderReview = () => (
    <div className="text-center space-y-6">
      <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto">
        <CheckCircle className="w-8 h-8 text-white" />
      </div>

      <div>
        <h2 className="text-2xl font-bold text-white mb-2">
          {language === 'traditional' ? '學習完成！' :
           language === 'simplified' ? '学习完成！' :
           'Learning Complete!'}
        </h2>
        <p className="text-gray-300">
          {language === 'traditional' ? '你已經學會了這個詞彙' :
           language === 'simplified' ? '你已经学会了这个词汇' :
           'You have learned this vocabulary'}
        </p>
      </div>

      <div className="bg-white/10 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4">{displayText.answer}</h3>
        <p className="text-gray-300 mb-2">{displayText.question}</p>
        <p className="text-gray-400 text-sm">{word.pronunciation}</p>
      </div>

      <div className="flex space-x-3">
        <button className="flex-1 flex items-center justify-center space-x-2 py-3 bg-blue-500 rounded-lg">
          <Volume2 className="w-5 h-5" />
          <span>
            {language === 'traditional' ? '再聽一次' :
             language === 'simplified' ? '再听一次' :
             'Listen Again'}
          </span>
        </button>
        <button 
          onClick={onNext}
          className="flex-1 py-3 bg-green-500 rounded-lg"
        >
          {language === 'traditional' ? '下一個詞' :
           language === 'simplified' ? '下一个词' :
           'Next Word'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="p-6">
      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex justify-between text-sm text-gray-400 mb-2">
          <span>{studySteps[currentStep].title}</span>
          <span>{currentStep + 1} / {studySteps.length}</span>
        </div>
        <div className="w-full bg-gray-700 rounded-full h-2">
          <div 
            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentStep + 1) / studySteps.length) * 100}%` }}
          ></div>
        </div>
      </div>

      {/* Content */}
      {currentStep === 0 && renderIntroduction()}
      {currentStep === 1 && renderPractice()}
      {currentStep === 2 && renderReview()}
    </div>
  );
};

export default StudyMode;
